import { Check, Loader2, Plus } from "lucide-react";
import { Button } from "components/common/buttons/Button";
import { formatBEDateToMediumDateString } from "utils";
import { cn } from "utils/cn";
import { VersionListProps } from "./VersionList.types";

export const VersionList = ({
  versions,
  liveVersionId,
  isLoading,
  isError,
  loadedVersionIds,
  loadingVersionId,
  onToggle,
  onRestore,
  onDelete,
  onMakeLive,
  onCompareDiff,
  isViewer = false,
}: VersionListProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-left text-[13px] text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading saved versions…
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-left text-[13px] text-danger">
        Couldn’t load saved versions. Close and reopen to try again.
      </p>
    );
  }

  if (versions.length === 0) {
    return (
      <p className="text-left text-[13px] text-muted-foreground">
        No saved versions yet. Save the current budget above to start comparing.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {versions.map((version) => {
        const isLoaded = loadedVersionIds.has(version.budget_id);
        const isLive = version.budget_id === liveVersionId;
        const isBusy = loadingVersionId === version.budget_id;
        const name = version.version_name?.trim() || `Version ${version.budget_id}`;
        const created = formatBEDateToMediumDateString(version.created_at);

        return (
          <div key={version.budget_id} className="flex items-center gap-1">
            <button
              key={version.budget_id}
              type="button"
              disabled={loadingVersionId !== null && !isBusy}
              onClick={() => onToggle(version)}
              aria-pressed={isLoaded}
              className={cn(
                "group flex items-center gap-2 rounded-lg border px-3 py-1.5 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary/50",
                "disabled:cursor-not-allowed disabled:opacity-40",
                isLoaded
                  ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
                  : "border-border bg-muted/50 text-foreground/80 hover:bg-muted"
              )}
            >
              <span className="flex h-4 w-4 items-center justify-center">
                {isBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isLoaded ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4 text-muted-foreground group-hover:text-foreground/80" />
                )}
              </span>
              <span className="flex flex-col">
                <span className="flex items-center gap-1.5 text-sm font-medium leading-tight">
                  {name}
                  {isLive ? (
                    <span className="rounded bg-muted px-1 py-px text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Live
                    </span>
                  ) : null}
                </span>
                {created ? (
                  <span className="text-[11px] leading-tight text-muted-foreground">{created}</span>
                ) : null}
              </span>
            </button>
            {!isViewer && !isLive ? (
              <div className="flex items-center gap-1">
                {onCompareDiff ? (
                  <Button
                    type="button"
                    color="gray"
                    size="xs"
                    onClick={() => onCompareDiff(version)}
                  >
                    Diff
                  </Button>
                ) : null}
                {onRestore ? (
                  <Button type="button" color="gray" size="xs" onClick={() => onRestore(version)}>
                    Restore
                  </Button>
                ) : null}
                {onMakeLive ? (
                  <Button type="button" color="gray" size="xs" onClick={() => onMakeLive(version)}>
                    Make live
                  </Button>
                ) : null}
                {onDelete ? (
                  <Button type="button" color="red" size="xs" onClick={() => onDelete(version)}>
                    Delete
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
